import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import ChatbotPreviewWithChat from '../../chatbots/components/ChatbotPreviewWithChat';
import { useChatbotStore } from '../../chatbots/stores/chatbotStore';
import { chatService } from '../../chatbots/services/chat.service';

const ChatbotDedicatedPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const chatbotId = id || '';
  const { visitorId } = useAuth();
  const { setChatbotData } = useChatbotStore();

  const [triggerMessage, setTriggerMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize chatbot using unified init API
  useEffect(() => {
    if (!chatbotId || !visitorId) return;

    const initialize = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log('🚀 Initializing demo chatbot with unified API...');
        const data = await chatService.initialize(chatbotId, visitorId);
        
        if (data) {
          // Transform init API response to match ChatbotBuilder structure
          const chatbotBuilder = {
            chatbot: {
              _id: data._id,
              name: data.name,
              description: data.description,
              logo: data.logo,
              isActive: data.isActive,
              status: data.status,
              roleId: data.roleId,
              assistantInstructions: data.assistantInstructions,
              isDummy: data.isDummy,
            },
            config: {
              modelName: data.modelName,
              welcomeTitle: data.welcomeTitle,
              welcomeMessage: data.welcomeMessage,
              inputPlaceholder: data.inputPlaceholder,
              questionList: data.questionList || [],
              theme: data.theme,
              primaryColor: data.primaryColor,
              secondaryColor: data.secondaryColor,
              textColor: data.textColor,
              size: data.size,
              position: data.position,
              windowStyle: data.windowStyle,
            },
            dataCollection: data.dataCollection || [],
            dataCompletionCompleted: data.dataCompletionCompleted || false,
            messages: data.messages || [],
            conversationId: data.conversationId || null,
            conversationStatus: data.conversationStatus || 'bot_active',
          };
          setChatbotData(chatbotBuilder);
        } else {
          setError('Failed to load chatbot data');
        }
      } catch (err) {
        console.error('❌ Demo initialization error:', err);
        setError('Failed to initialize chatbot');
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [chatbotId, visitorId, setChatbotData]);
  
if (isLoading) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-primary/5 to-secondary/5">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

if (error) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-primary/5 to-secondary/5">
      <div className="p-4 text-center">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Error</h2>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen ">
      <div className=" overflow-hidden">
        <div className="h-[600px]">
          <ChatbotPreviewWithChat
            triggerMessage={triggerMessage}
            onMessageSent={() => setTriggerMessage('')}
            showFrame={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatbotDedicatedPage;